import { File, Paths } from 'expo-file-system';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
} from 'expo-router';
import { shareAsync } from 'expo-sharing';
import { useSetupDirectDeposit } from 'features/directDeposit/hooks/useSetupDirectDeposit';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import PdfViewer from 'shared/components/PdfRenderer/PdfViewer';
import Button from 'shared/components/vxl/Button/Button';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import { isWeb } from 'shared/constants/platform';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import DefaultPageDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/DefaultPageDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

const DirectDepositFormScreen = () => {
  const { t: translatedText } = useTranslation('directDeposit');
  const { id } = useLocalSearchParams<{
    id: string;
  }>();
  const [sharingInProgress, setSharingInProgress] = useState(false);
  const { bottom } = useSafeAreaInsets();

  const { defaultTitle } = useDefaultErrorStrings();

  const { fetching, error, pdfURL, directDepositError, reTry } =
    useSetupDirectDeposit(id);

  const onPressSendForm = async () => {
    try {
      setSharingInProgress(true);
      if (!pdfURL) {
        return;
      }
      const destination = new File(Paths.cache, 'depositForm.pdf');
      const downloadedFile = await File.downloadFileAsync(pdfURL, destination, {
        idempotent: true,
      });
      await shareAsync(downloadedFile.uri);
      setSharingInProgress(false);
    } catch {
      setSharingInProgress(false);
      // TODO JT-122752: Add sentry/error analytics
    }
  };

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
          title: translatedText('header'),
        }}
      />
      <View style={styles.title}>
        <BodyBaseText>{translatedText('options.sendFormTitle')}</BodyBaseText>
      </View>
      {pdfURL ? <PdfViewer url={pdfURL} /> : null}
      {!isWeb ? (
        <View style={styles.buttonWrapper}>
          <Button
            label={translatedText('options.sendFormButton')}
            isLoading={sharingInProgress}
            type="PRIMARY"
            onPress={onPressSendForm}
            analyticsId="sendForm"
          />
        </View>
      ) : null}
      <ScreenTransitionOverlay
        isLoading={fetching}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          error || directDepositError ? (
            <DefaultPageDownErrorMessage
              error={error || directDepositError}
              title={directDepositError?.title || defaultTitle}
              message={directDepositError?.message || error?.message}
              tryAgainAction={reTry}
            />
          ) : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  buttonWrapper: {
    padding: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  title: {
    padding: theme.spacing.m,
    paddingBottom: theme.spacing.s,
  },
});

export default DirectDepositFormScreen;
